import {
  useIsMutating,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { SMS_TEMPLATES_QUERY_KEY } from 'constants/QueryKeys.constants';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { setIsFunctionsDirty } from 'ducks/functionsForm/functionsFormSlice';

import api from 'api';
import type { HTTPError } from 'api/error';
import type {
  FunctionCall,
  FunctionExecutionRequest,
  FunctionExecutionResponse,
  FunctionsDeployResponse,
  GetFunctionsResponse,
} from 'api/resources/functions/types';
import { curryPostFunction } from 'api/utils';
import { useAppSelector, useLocalDispatch } from 'hooks/reduxHooks';
import { useDraft } from 'hooks/useDraft';
import useDraftMutation from 'hooks/useDraftMutation';
import useProjectRoute from 'hooks/useProjectRoute';
import { useToast } from 'hooks/useToast';
import { getProjectQueryKey } from 'utils/query';

dayjs.extend(utc);

const DEPLOY_FUNCTIONS_MUTATION_KEY = 'functions-deploy';

const updateFunctions = (
  func: FunctionCall,
  functions: FunctionCall[] = [],
  allowInactive: boolean = true,
) => {
  const funcs: FunctionCall[] = [];
  if (func.active || allowInactive) {
    funcs.push(func);
  }
  funcs.push(...functions.filter(f => f.function_id !== func.function_id));
  return funcs;
};

const useFunctionActions = () => {
  const toast = useToast();
  const queryClient = useQueryClient();

  const { accountId, projectId } = useProjectRoute();
  const isFunctionsDirty = useAppSelector(
    state => state.functionsForm.isFunctionsDirty,
  );

  const dispatch = useLocalDispatch();
  const { onProjectUpdate } = useDraft();

  const functionsQueryKey = [accountId, projectId, 'functions'];
  const postFunctionMutationKey = [accountId, projectId, 'function'];

  const deployingFunctionsMutationKey = [
    accountId,
    projectId,
    DEPLOY_FUNCTIONS_MUTATION_KEY,
  ];

  const { mutate: deleteFunction, isLoading: isDeletingFunction } =
    useDraftMutation<
      undefined,
      HTTPError,
      { functionId: string },
      { previousFunctions: GetFunctionsResponse }
    >(curryPostFunction(api.deleteFunction)({ accountId, projectId }), {
      mutationKey: functionsQueryKey,
      meta: {
        invalidates: [[accountId, projectId, ...SMS_TEMPLATES_QUERY_KEY]],
      },
      onMutate: ({ functionId }) => {
        const previousFunctions =
          queryClient.getQueryData<GetFunctionsResponse>(functionsQueryKey);
        queryClient.setQueryData(
          functionsQueryKey,
          (oldData: GetFunctionsResponse | undefined) => {
            if (!oldData) {
              return;
            }
            return {
              functions: oldData.functions.map(func =>
                func.function_id !== functionId
                  ? func
                  : { ...func, active: false },
              ),
              active_functions: oldData.active_functions.filter(
                func => func.function_id !== functionId,
              ),
            };
          },
        );

        onProjectUpdate();

        if (!previousFunctions) {
          return;
        }

        return { previousFunctions };
      },
      onError: (e, _variables, context) => {
        if (e.code === 'FunctionsDeploymentHasErrors') {
          queryClient
            .invalidateQueries(functionsQueryKey)
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          toast.success({
            title: 'Successfully deleted function',
          });

          setTimeout(
            () =>
              toast.error({
                title: 'Failed to deploy functions',
              }),
            1000,
          );
        } else {
          toast.error({
            title: 'Delete function failed',
          });

          if (context?.previousFunctions) {
            queryClient.setQueryData(
              functionsQueryKey,
              context.previousFunctions,
            );
          } else {
            queryClient
              .refetchQueries(functionsQueryKey)
              .catch(e =>
                console.error('Error occurred. Could not refetch queries', e),
              );
          }
        }
        queryClient
          .invalidateQueries(getProjectQueryKey(accountId, projectId))
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
      },
    });

  const {
    mutate: postFunction,
    mutateAsync: postFunctionAsync,
    isSuccess: isPostFunctionSuccess,
  } = useDraftMutation(
    curryPostFunction(api.postFunction)({ accountId, projectId }),
    {
      mutationKey: postFunctionMutationKey,
      meta: {
        invalidates: [[accountId, projectId, ...SMS_TEMPLATES_QUERY_KEY]],
      },
      onSuccess: func => {
        queryClient.setQueryData(
          functionsQueryKey,
          (state: GetFunctionsResponse | undefined) => {
            if (!state) {
              return state;
            }

            return {
              functions: updateFunctions(func, state?.functions),
              active_functions: updateFunctions(
                func,
                updateFunctions(func, state?.active_functions),
                false,
              ),
            };
          },
        );
        queryClient
          .invalidateQueries([
            accountId,
            projectId,
            'functions',
            func.function_id,
          ])
          .catch(e =>
            console.error('Error occurred. Could not invalidate queries', e),
          );
        dispatch(setIsFunctionsDirty(true));
        onProjectUpdate();
      },
      onError: (e: HTTPError, variables) => {
        if (e.code === 'FunctionsDeploymentHasErrors') {
          queryClient
            .invalidateQueries([
              ...functionsQueryKey,
              variables.func.function_id,
            ])
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          toast.error({
            title: 'Failed to deploy functions',
          });
        } else {
          toast.error({
            title: `${
              variables.func.function_id ? 'Update' : 'Add'
            } function failed`,
          });
        }
      },
    },
  );

  const {
    mutate: duplicateFunction,
    mutateAsync: duplicateFunctionAsync,
    isLoading: isDuplicatingFunction,
  } = useDraftMutation(
    curryPostFunction(api.duplicateFunction)({ accountId, projectId }),
    {
      mutationKey: functionsQueryKey,
      meta: {
        invalidates: [[accountId, projectId, ...SMS_TEMPLATES_QUERY_KEY]],
      },
      onSuccess: data => {
        queryClient.setQueryData(
          functionsQueryKey,
          (
            {
              functions: oldFunctions,
              active_functions: oldActiveFunctions,
            }: GetFunctionsResponse | undefined = {
              functions: [],
              active_functions: [],
            },
          ) => ({
            functions: [data, ...oldFunctions],
            active_functions: [data, ...oldActiveFunctions],
          }),
        );

        toast.success({
          title: 'Successfully duplicated function',
        });
        onProjectUpdate();
      },
      onError: (e: HTTPError, variables) => {
        if (e.code === 'FunctionsDeploymentHasErrors') {
          queryClient
            .invalidateQueries([...functionsQueryKey, variables.functionId])
            .catch(e =>
              console.error('Error occurred. Could not invalidate queries', e),
            );
          toast.success({
            title: 'Successfully duplicated function',
          });

          setTimeout(
            () =>
              toast.error({
                title: 'Failed to deploy functions',
              }),
            1000,
          );
        } else {
          toast.error({
            title: 'Duplicate function failed',
          });
        }
      },
    },
  );

  const {
    mutateAsync: executeFunction,
    data: functionExecution,
    isLoading: isLoadingExecuteFunction,
  } = useMutation<
    FunctionExecutionResponse,
    HTTPError,
    { functionId: string } & FunctionExecutionRequest
  >(
    ({ functionId, args }) =>
      api.executeFunction({
        accountId,
        projectId,
        functionId,
        body: { args },
      }),
    {
      onError: () => {
        toast.error({
          title: 'Failed to execute function test. Please try again later',
        });
      },
    },
  );

  const { mutate: deployFunctions } = useMutation<
    FunctionsDeployResponse,
    HTTPError
  >(
    () =>
      api.deployFunctions({
        accountId,
        projectId,
      }),
    {
      onSuccess: () => {
        dispatch(setIsFunctionsDirty(false));
      },
      onError: () => {
        toast.error({
          title: 'Failed to deploy functions',
        });
      },
      mutationKey: deployingFunctionsMutationKey,
    },
  );

  const isPostingFunction = !!useIsMutating(postFunctionMutationKey);
  const isDeployingFunctions = !!useIsMutating(deployingFunctionsMutationKey);

  return {
    deleteFunction,
    postFunction,
    postFunctionAsync,
    duplicateFunction,
    duplicateFunctionAsync,
    deployFunctions,
    executeFunction,
    isFunctionsDirty,
    isDeployingFunctions,
    isDuplicatingFunction,
    isDeletingFunction,
    isPostingFunction,
    isPostFunctionSuccess,
    isLoadingExecuteFunction,
    functionExecution,
  };
};

export default useFunctionActions;
